import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useBranding } from '../hooks/useBranding';
import { BrandingConfig } from '../types/branding';

export interface CompanyTheme {
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  textOnPrimary: string;
  showCompanyName: boolean;
}

interface ThemeContextType {
  theme: CompanyTheme;
  setTheme: (theme: CompanyTheme) => void;
  isLoading: boolean;
  branding?: BrandingConfig;
  updateTheme: (branding: BrandingConfig) => void;
}

const defaultTheme: CompanyTheme = {
  primaryColor: '#3B82F6',
  secondaryColor: '#1E40AF',
  accentColor: '#60A5FA',
  textOnPrimary: '#FFFFFF',
  showCompanyName: true,
};

const ThemeContext = createContext<ThemeContextType>({
  theme: defaultTheme,
  setTheme: () => {},
  isLoading: true,
  updateTheme: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

/**
 * Apply theme colors to CSS variables
 */
const applyTheme = (theme: CompanyTheme) => {
  document.documentElement.style.setProperty('--color-primary', theme.primaryColor);
  document.documentElement.style.setProperty('--color-secondary', theme.secondaryColor);
  document.documentElement.style.setProperty('--color-accent', theme.accentColor);
  document.documentElement.style.setProperty('--color-text-on-primary', theme.textOnPrimary);
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { branding, isLoading: isBrandingLoading } = useBranding();
  const [theme, setThemeState] = useState<CompanyTheme>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);
  const cacheRef = useRef<BrandingConfig | null>(null);

  // Load theme from branding API
  useEffect(() => {
    if (!isBrandingLoading) {
      if (branding) {
        // Cache branding configuration in memory
        cacheRef.current = branding;
        
        const newTheme: CompanyTheme = {
          logoUrl: branding.logoUrl,
          primaryColor: branding.primaryColor,
          secondaryColor: branding.secondaryColor,
          accentColor: branding.accentColor,
          textOnPrimary: branding.textOnPrimary,
          showCompanyName: branding.showCompanyName,
        };
        
        setThemeState(newTheme);
        applyTheme(newTheme);
      } else {
        // Use default theme if no branding configured
        setThemeState(defaultTheme);
        applyTheme(defaultTheme);
      }
      setIsLoading(false);
    }
  }, [branding, isBrandingLoading]);

  // Apply theme whenever it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = (newTheme: CompanyTheme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
  };

  const updateTheme = (newBranding: BrandingConfig) => {
    cacheRef.current = newBranding;
    const newTheme: CompanyTheme = {
      logoUrl: newBranding.logoUrl,
      primaryColor: newBranding.primaryColor,
      secondaryColor: newBranding.secondaryColor,
      accentColor: newBranding.accentColor,
      textOnPrimary: newBranding.textOnPrimary,
      showCompanyName: newBranding.showCompanyName,
    };
    setThemeState(newTheme);
    applyTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isLoading, branding: cacheRef.current || undefined, updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
